import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

// ─── Customer Users (client_viewer role) ─────────────────────────────────

export function useCustomerUsers(active = true) {
  return useQuery({
    queryKey: ["customer-users", active],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_account")
        .select(`
          id, org_id, display_name, email, is_active, created_at, client_id,
          client:client!user_account_client_id_fkey(id, name),
          portal_client_access(client_id),
          user_role!inner(
            role_id,
            role:role_id(name)
          )
        `)
        .eq("is_active", active)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Filter to only client_viewer users
      return (data ?? []).filter((u: any) =>
        u.user_role?.some((ur: any) => ur.role?.name === "client_viewer")
      );
    },
  });
}

/**
 * Set which clients (brands) a customer login can see in the portal.
 * portal_client_access is the source of truth; user_account.client_id stays the
 * primary client (CustomerPortal / accept-invitation read it), so it's moved to
 * another selected client — or cleared — if it's no longer in the list.
 */
export function useSetCustomerClients() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      orgId,
      primaryClientId,
      currentClientIds,
      clientIds,
    }: {
      userId: string;
      orgId: string;
      primaryClientId: string | null;
      currentClientIds: string[];
      clientIds: string[];
    }) => {
      const toAdd = clientIds.filter((id) => !currentClientIds.includes(id));
      const toRemove = currentClientIds.filter((id) => !clientIds.includes(id));

      if (toAdd.length > 0) {
        const { error } = await supabase
          .from("portal_client_access")
          .insert(toAdd.map((client_id) => ({ org_id: orgId, user_account_id: userId, client_id })));
        if (error) throw error;
      }
      if (toRemove.length > 0) {
        const { error } = await supabase
          .from("portal_client_access")
          .delete()
          .eq("user_account_id", userId)
          .in("client_id", toRemove);
        if (error) throw error;
      }

      const nextPrimary =
        primaryClientId && clientIds.includes(primaryClientId) ? primaryClientId : clientIds[0] ?? null;
      if (nextPrimary !== primaryClientId) {
        const { error } = await supabase
          .from("user_account")
          .update({ client_id: nextPrimary })
          .eq("id", userId);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-users"] });
      toast({ title: "Customer updated", description: "Client access saved." });
    },
    onError: (error: Error) => {
      queryClient.invalidateQueries({ queryKey: ["customer-users"] });
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}

// ─── Deactivate / Reactivate Customer ─────────────────────────────────

export function useDeactivateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase
        .from("user_account")
        .update({ is_active: false })
        .eq("id", userId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-users"] });
      toast({ title: "Customer removed", description: "The customer has been deactivated." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}

export function useReactivateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase
        .from("user_account")
        .update({ is_active: true })
        .eq("id", userId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-users"] });
      toast({ title: "Customer reactivated", description: "The customer account is active again." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}

// ─── Customer Invitations (pending/expired with client_viewer role) ─────

export function useCustomerInvitations() {
  return useQuery({
    queryKey: ["customer-invitations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_invitation")
        .select(`
          *,
          invited_by:user_account!invited_by(display_name, email),
          client:client!user_invitation_client_id_fkey(id, name)
        `)
        .in("status", ["pending", "expired"])
        .contains("roles", ["client_viewer"])
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useRevokeCustomerInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await supabase
        .from("user_invitation")
        .update({ status: "revoked" })
        .eq("id", invitationId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-invitations"] });
      toast({ title: "Invitation revoked", description: "The invitation has been cancelled." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}

export function useResendCustomerInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { data: invitation } = await supabase
        .from("user_invitation")
        .select("email, roles, invited_by, client_id")
        .eq("id", invitationId)
        .single();

      if (!invitation) throw new Error("Invitation not found");

      // Revoke old
      await supabase.from("user_invitation").update({ status: "revoked" }).eq("id", invitationId);

      // Create new
      const { data, error } = await supabase.functions.invoke("send-user-invitation", {
        body: {
          email: invitation.email,
          roles: invitation.roles,
          invitedBy: invitation.invited_by,
          ...(invitation.client_id ? { clientId: invitation.client_id } : {}),
        },
      });

      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-invitations"] });
      toast({ title: "Invitation resent", description: "A new invitation email has been sent." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}

// ─── Invite Customer (direct email invitation) ─────────────────────────

export function useCreateCustomerInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ email, clientId }: { email: string; clientId: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: userAccount } = await supabase
        .from("user_account")
        .select("id")
        .eq("external_auth_user", user.id)
        .single();

      if (!userAccount) throw new Error("User account not found");

      const { data, error } = await supabase.functions.invoke("send-user-invitation", {
        body: {
          email,
          roles: ["client_viewer"],
          invitedBy: userAccount.id,
          clientId,
        },
      });

      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-users"] });
      queryClient.invalidateQueries({ queryKey: ["customer-invitations"] });
      toast({ title: "Customer invited", description: "An invitation email has been sent." });
    },
    onError: (error: Error) => {
      toast({ title: "Error sending invitation", description: error.message, variant: "destructive" });
    },
  });
}

// ─── Customer Access Codes (beta codes with client_viewer role) ──────────

export function useCustomerAccessCodes() {
  return useQuery({
    queryKey: ["customer-access-codes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("beta_access_code")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Filter to codes that include client_viewer in their roles
      return (data ?? []).filter((code: any) => {
        const roles = Array.isArray(code.roles) ? code.roles : [];
        return roles.includes("client_viewer");
      });
    },
  });
}

export function useCustomerCodeSignups(codeId: string | undefined) {
  return useQuery({
    queryKey: ["customer-code-signups", codeId],
    enabled: !!codeId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("beta_signup")
        .select(`
          id, signed_up_at,
          user_account:user_account_id(id, display_name, email)
        `)
        .eq("beta_code_id", codeId!)
        .order("signed_up_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });
}

export function useCreateCustomerCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      code,
      label,
      maxUses,
      expiresAt,
    }: {
      code: string;
      label: string;
      maxUses?: number | null;
      expiresAt?: string | null;
    }) => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error("Not authenticated");

      const { data: userAccount } = await supabase
        .from("user_account")
        .select("id, org_id")
        .eq("external_auth_user", userData.user.id)
        .single();
      if (!userAccount) throw new Error("User account not found");

      const { data, error } = await supabase
        .from("beta_access_code")
        .insert({
          code: code.toUpperCase().trim(),
          label,
          max_uses: maxUses ?? null,
          expires_at: expiresAt ?? null,
          roles: ["client_viewer"],
          org_id: userAccount.org_id,
          created_by: userAccount.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-access-codes"] });
      toast({ title: "Access code created", description: "Customer access code is ready." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}

export function useToggleCustomerCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from("beta_access_code")
        .update({ is_active: isActive })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-access-codes"] });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}
